import React from 'react';
import {
  Autocomplete, TextField, Box, Typography, Paper, Chip, Checkbox,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  useTheme, alpha,
} from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material';
import type { SelectOption } from './SearchableSelect';

/**
 * SearchableMultiSelect — the multi-select twin of `SearchableSelect`.
 *
 * Same popover chrome, same option row shape, same font sizes. The only
 * difference from the single-select variant is that the field shows the
 * selected values as compact chips (or an "N selected" pill when the list
 * is long) and each option row carries a checkbox.
 *
 * API mirrors `SearchableSelect` but takes `values` / `onChange(values[])`
 * instead of a single value.
 */

export interface SearchableMultiSelectProps {
  label?: string;
  values: string[];
  onChange: (values: string[]) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  minWidth?: number | string;
  /** Fixed width — keeps the field (and its popover) from resizing as
   *  chips are added or removed. */
  width?: number | string;
  helperText?: string;
  error?: boolean;
  /**
   * When the number of selected items exceeds this, we collapse them into
   * an "N selected" summary chip instead of rendering each chip. Default: 2.
   */
  maxChipsShown?: number;
  /** Optional section headers in the popover ("In progress", "Final" …).
   *  Options must already be ordered by group. */
  groupBy?: (option: SelectOption) => string;
}

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
  label,
  values,
  onChange,
  options,
  placeholder,
  disabled,
  size = 'small',
  fullWidth = false,
  minWidth = 200,
  width,
  helperText,
  error,
  maxChipsShown = 2,
  groupBy,
}) => {
  const theme = useTheme();

  const selectedOptions = React.useMemo(
    () => options.filter(o => values.includes(o.value)),
    [options, values],
  );

  return (
    <Autocomplete<SelectOption, true, false, false>
      multiple
      disableCloseOnSelect
      value={selectedOptions}
      onChange={(_, next) => onChange(next.map(o => o.value))}
      options={options}
      getOptionLabel={o => o.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      getOptionDisabled={o => Boolean(o.disabled)}
      groupBy={groupBy}
      disabled={disabled}
      size={size}
      fullWidth={fullWidth}
      // Popover style matches SearchableSelect exactly — subtle border, soft
      // shadow, comfortable option rows.
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            mt: 0.5,
            borderRadius: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 24px rgba(0, 0, 0, 0.45)'
              : '0 8px 24px rgba(15, 23, 42, 0.08)',
            overflow: 'hidden',
            '& .MuiAutocomplete-listbox': {
              py: 0.5,
              '& .MuiAutocomplete-groupLabel': {
                lineHeight: '28px',
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                color: theme.palette.text.secondary,
              },
              '& .MuiAutocomplete-option': {
                minHeight: 34,
                py: 0.5,
                px: 1,
                fontSize: '0.825rem',
                fontWeight: 500,
                gap: 0.5,
                transition: 'background-color 80ms ease',
                '&[aria-selected="true"]': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '&.Mui-focused': {
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                  },
                },
              },
            },
          },
        },
      }}
      PaperComponent={Paper}
      sx={{
        minWidth,
        width,
        // Chips stay on one line so the field keeps the single-select height.
        '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap', overflow: 'hidden' },
        '& .MuiAutocomplete-inputRoot .MuiChip-root': { maxWidth: 160, flexShrink: 1, minWidth: 0 },
        '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 24 },
      }}
      renderTags={(tagValue, getTagProps) => {
        // Collapse to a single summary chip when the selection is long.
        // Keeps the field's height consistent with the single-select variant.
        if (tagValue.length > maxChipsShown) {
          return (
            <Chip
              size="small"
              label={`${tagValue.length} selected`}
              sx={{
                height: 22, fontSize: '0.72rem', fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.dark,
                '& .MuiChip-label': { px: 0.9 },
              }}
              onDelete={() => onChange([])}
            />
          );
        }
        return tagValue.map((opt, index) => {
          const tagProps = getTagProps({ index });
          return (
            <Chip
              {...tagProps}
              key={opt.value}
              size="small"
              label={opt.label}
              sx={{
                height: 22, fontSize: '0.72rem', fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.dark,
                '& .MuiChip-label': { px: 0.9 },
                '& .MuiChip-deleteIcon': {
                  color: alpha(theme.palette.primary.dark, 0.7),
                  '&:hover': { color: theme.palette.primary.dark },
                },
              }}
            />
          );
        });
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={label}
          placeholder={values.length === 0 ? placeholder : undefined}
          error={error}
          helperText={helperText}
          InputLabelProps={{ shrink: true, ...(params.InputLabelProps || {}) }}
        />
      )}
      renderOption={(props, option: SelectOption, state: AutocompleteRenderOptionState) => (
        <Box
          component="li"
          {...props}
          key={option.value}
          sx={{ '&.MuiAutocomplete-option': { alignItems: 'center' } }}
        >
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            size="small"
            checked={state.selected}
            sx={{ p: 0.5, mr: 0.5 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={state.selected ? 600 : 500}
              sx={{ fontSize: '0.825rem', lineHeight: 1.3 }}
              noWrap
            >
              {option.label}
            </Typography>
            {option.secondary && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.15 }}
                noWrap
              >
                {option.secondary}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    />
  );
};

export default SearchableMultiSelect;
